import { Button, Icon, Row, Text, Tooltip } from '@amzn/meridian';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import { useUniqueElementId } from 'app.util';
import type { FC } from 'react';

/**
 * Action buttons component for DMCC field sharing controls.
 *
 * Displays either a "Change Sharing" button for configurable fields or a locked state
 * message with tooltip for fields that cannot have their sharing configuration modified.
 *
 * ## Behavior:
 * - **Unlocked State**: Shows interactive button that opens the sharing configuration modal
 * - **Locked State**: Shows informational text with tooltip explaining why sharing cannot be changed
 *
 * ## Locked State Conditions:
 * Sharing is typically locked when:
 * - Field has a forced sharing mode that cannot be overridden by users
 * - Field sharing is controlled by system/campaign-level constraints
 * - User lacks permissions to modify sharing configuration
 * - Configuration is inherited from parent elements
 *
 * ## Accessibility:
 * - Uses proper ARIA labeling with `aria-describedby` for tooltip association
 * - Provides keyboard accessibility for interactive elements
 * - Includes descriptive tooltip content for screen readers
 *
 * @param props - Component props
 * @param props.isLocked - Whether sharing configuration is locked and cannot be modified
 * @param props.onOpenModal - Callback function to open the sharing configuration modal
 */
export const SharingActionButtons: FC<{
  isLocked: boolean;
  onOpenModal: () => void;
}> = ({ isLocked, onOpenModal }) => {
  const tooltipId = useUniqueElementId('sharing-actions-buttons-tooltip');

  return isLocked ? (
    <Tooltip
      delay={500}
      id={tooltipId}
      position="bottom"
      title="Values in this field are required to be shared across the listed variations in the campaign."
    >
      <Row spacing="300" spacingInset="0 0 0 300">
        <Text aria-describedby={tooltipId} color="secondary" type="b200">
          Sharing cannot be changed
        </Text>
        <Icon color="secondary" tokens={infoKnockoutTokens} />
      </Row>
    </Tooltip>
  ) : (
    <Button data-cy="change-shared-value-button" onClick={onOpenModal} size="small" type="link">
      Change Sharing
    </Button>
  );
};
